import { useState, type ReactNode } from "react";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";
import { Clear, ExpandLess, ExpandMore, FilterList } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

export type FilterPanelProps = {
  children: ReactNode;
  activeCount?: number;
  defaultOpen?: boolean;
  onClear?: () => void;
  actions?: ReactNode;
  flush?: boolean;
  embedded?: boolean;
  bodySx?: SxProps<Theme>;
};

export default function FilterPanel({
  children,
  activeCount = 0,
  defaultOpen = false,
  onClear,
  actions,
  flush = false,
  embedded = false,
  bodySx,
}: FilterPanelProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const hasActive = activeCount > 0;

  const body = (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 1.5,
          py: 1,
          cursor: "pointer",
          bgcolor: (theme) =>
            open || hasActive ? theme.palette.action.hover : "transparent",
        }}
        onClick={() => setOpen((current) => !current)}
      >
        <Badge color="primary" badgeContent={hasActive ? activeCount : 0} max={99}>
          <FilterList fontSize="small" color={hasActive ? "primary" : "action"} />
        </Badge>
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
          {t("common.filters")}
        </Typography>
        {hasActive && onClear ? (
          <Button
            size="small"
            color="inherit"
            startIcon={<Clear fontSize="small" />}
            onClick={(event) => {
              event.stopPropagation();
              onClear();
            }}
            sx={{ textTransform: "none" }}
          >
            {t("common.clearFilters")}
          </Button>
        ) : null}
        {actions ? (
          <Box
            onClick={(event) => event.stopPropagation()}
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
          >
            {actions}
          </Box>
        ) : null}
        <IconButton
          size="small"
          aria-label={open ? t("common.collapseFilters") : t("common.expandFilters")}
          aria-expanded={open}
          sx={{ width: 30, height: 30 }}
        >
          {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Stack>
      <Collapse in={open} unmountOnExit={false}>
        <Box
          sx={
            {
              px: 1.5,
              pb: 1.5,
              pt: 1.25,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 2,
              alignItems: "start",
              ...(bodySx as object),
            } as SxProps<Theme>
          }
        >
          {children}
        </Box>
      </Collapse>
    </>
  );

  if (embedded) {
    return (
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        {body}
      </Box>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: flush ? 0 : 2,
        borderRadius: flush ? 0 : 1.5,
        borderLeft: flush ? 0 : undefined,
        borderRight: flush ? 0 : undefined,
        borderTop: flush ? 0 : undefined,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      {body}
    </Paper>
  );
}
